/**
 * Debug 模式 Composable
 *
 * 提供超管调试模式功能：
 * - URL 带 ?debug=true 时提示输入超管密码
 * - 验证通过后，所有 API 请求自动附带 exclude_internal=true 和 debug_token
 * - SSE 连接也会附带 debug_token 参数
 */

import { ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { request } from '@/api/request'

/** debug token（模块级共享状态，所有 composable 实例共用） */
const debugToken = ref<string>('')

/** 是否处于 debug 模式 */
const isDebug = ref(false)

/** Debug 验证接口响应类型 */
interface DebugVerifyResponse {
  debug_token: string
  expires_in: number
}

/**
 * Debug 模式 Composable
 *
 * @returns debug 模式相关的响应式状态和方法
 */
export function useDebugMode() {
  const route = useRoute()
  const router = useRouter()

  /**
   * 初始化 debug 模式
   *
   * 检查 URL 中是否包含 ?debug=true，如果是则弹出密码输入框，
   * 验证通过后进入 debug 模式。
   */
  async function initDebugMode(): Promise<void> {
    if (route.query.debug !== 'true') return

    const secret = window.prompt('请输入超管密码')
    if (!secret) {
      router.replace({ query: {} })
      return
    }

    try {
      const data = await request<DebugVerifyResponse>(
        '/api/v1/events/debug/verify',
        {
          method: 'POST',
          data: { secret },
          requireAuth: false,
        }
      )
      debugToken.value = data.debug_token
      isDebug.value = true
      console.log('[Debug] debug 模式已激活')
    } catch {
      window.alert('超管密码错误')
      router.replace({ query: {} })
    }
  }

  /**
   * 将 debug 相关参数附加到查询参数中
   *
   * 所有 dashboard API 调用默认带 exclude_internal=true，
   * 如果 debug 模式激活，还会带 debug_token。
   *
   * @param params - 原始查询参数
   * @returns 附加 debug 参数后的查询参数
   */
  function appendDebugParams(
    params: Record<string, string | number>
  ): Record<string, string | number> {
    const result: Record<string, string | number> = {
      ...params,
      exclude_internal: 'true',
    }
    if (debugToken.value) {
      result.debug_token = debugToken.value
    }
    return result
  }

  return { isDebug, debugToken, initDebugMode, appendDebugParams }
}

/**
 * 在 router guard 中初始化 debug 模式（组件渲染前执行）
 *
 * 解决 Vue onMounted 执行顺序问题：child onMounted 先于 parent 执行，
 * 导致子组件的 API 请求在 debug token 获取前就已发出。
 * 在 router beforeEach 中调用此函数，确保 token 在组件渲染前就绑定好。
 *
 * @param query - route.query 对象
 * @param replaceFn - 替换 URL 的函数（去掉 debug 参数）
 */
export async function initDebugFromRoute(
  query: Record<string, string | string[] | undefined>,
  replaceFn: (path: string) => void
): Promise<void> {
  if (query.debug !== 'true') return

  const secret = window.prompt('请输入超管密码')
  if (!secret) {
    replaceFn('/')
    return
  }

  try {
    const data = await request<DebugVerifyResponse>(
      '/api/v1/events/debug/verify',
      {
        method: 'POST',
        data: { secret },
        requireAuth: false,
      }
    )
    debugToken.value = data.debug_token
    isDebug.value = true
    console.log('[Debug] debug 模式已激活（router guard）')
  } catch {
    window.alert('超管密码错误')
    replaceFn('/')
  }
}

/**
 * 获取当前 debug token（供非 composable 上下文使用，如 API 模块）
 *
 * @returns 当前 debug token，未激活时为空字符串
 */
export function getDebugToken(): string {
  return debugToken.value
}

/**
 * 获取当前是否处于 debug 模式（供非 composable 上下文使用）
 */
export function getIsDebug(): boolean {
  return isDebug.value
}

/**
 * 构建带 debug 参数的查询字符串
 *
 * 供 API 模块直接使用，自动附加 exclude_internal 和 debug_token 参数。
 *
 * @param baseParams - 原始查询参数
 * @returns 附加 debug 参数后的查询参数
 */
export function withDebugParams(
  baseParams: Record<string, string | number>
): Record<string, string | number> {
  const result: Record<string, string | number> = {
    ...baseParams,
    exclude_internal: 'true',
  }
  if (debugToken.value) {
    result.debug_token = debugToken.value
  }
  return result
}
